import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Loader2, Droplets } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LoginPage: React.FC = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@silikprotek.com");
  const [password, setPassword] = useState("Silik2025");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, mfaRequired ? mfaCode : undefined);
      navigate("/");
    } catch (err: any) {
      if ((err.message || "").includes("mfa_required")) {
        setMfaRequired(true);
        setError("Ingresa el código de tu app de autenticación (MFA).");
      } else {
        setError(err.message || "No se pudo iniciar sesión");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left visual */}
      <div className="relative hidden w-1/2 overflow-hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1687392946859-cebb261f01f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHx3YXRlciUyMHByb3RlY3Rpb24lMjBhYnN0cmFjdCUyMGJsdWV8ZW58MHx8fHwxNzg3MDk0OTA4fDA&ixlib=rb-4.1.0&q=85"
          alt="Silik Prötek"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 brand-gradient opacity-90" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-white p-1.5">
              <img src="/silik-icono.png" alt="Silik Prötek" className="h-full w-full object-contain" />
            </div>
            <span className="text-lg font-black tracking-tight" style={{ fontFamily: "Bodoni Moda" }}>
              Silik Prötek
            </span>
          </div>
          <div className="max-w-md">
            <h1 className="text-4xl font-black leading-tight tracking-tighter" style={{ fontFamily: "Bodoni Moda" }}>
              Control de caja, ventas y deuda en un solo lugar.
            </h1>
            <p className="mt-4 text-white/80">
              Centraliza Shopify, Amazon y Mercado Libre. Separa lo vendido, lo cobrado, lo gastado y la
              deuda con trazabilidad total en MXN.
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-white/70">
            <Droplets className="h-4 w-4" strokeWidth={1.5} />
            Tecnología alemana · Mercado México
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center bg-slate-50 px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <img src="/silik-logo.png" alt="Silik Prötek" className="mb-6 h-10 object-contain lg:hidden" />
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">Iniciar sesión</h2>
            <p className="mt-1 text-sm text-slate-500">Ingresa tus credenciales para continuar.</p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Correo
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 rounded-none border-slate-300 focus-visible:ring-[#DD0000]/30"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 rounded-none border-slate-300 focus-visible:ring-[#DD0000]/30"
                required
              />
            </div>

            {mfaRequired && (
              <div>
                <Label htmlFor="mfa" className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                  Código MFA
                </Label>
                <Input
                  id="mfa"
                  type="text"
                  inputMode="numeric"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  data-testid="login-mfa-input"
                  placeholder="123456"
                  className="mt-1.5 rounded-none border-slate-300 tracking-[0.3em] focus-visible:ring-[#DD0000]/30"
                  autoFocus
                />
              </div>
            )}

            {error && (
              <div className="rounded-none border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-none bg-[#DD0000] py-2.5 font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Entrar
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-400">
            Acceso demo: admin@silikprotek.com · Silik2025
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
